import React, { Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Col, Container, Row, Label, Input, Spinner } from 'reactstrap';
import BreadCrumb from '../components/Common/BreadCrumb';
import Flatpickr from "react-flatpickr";
import dateFormat from "dateformat";
import moment from "moment";
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';

import { createDeepClone } from "../utils/index";

import CardLoader from "../components/CardLoader";
import ActionList from "../components/ActionList";
import ServiceDocumentGrid from "../components/ServiceDocumentGrid";

import { useDispatch, useSelector } from "react-redux";
import * as auditorActions from "../actions/AuditorActions";

const AuditorEditScreen  = (props) => {
    const { register, clearErrors, reset, trigger, formState: { errors } } = useForm({mode: 'all'});

    const navigate = useNavigate();
    const { id } = useParams();

    const dispatch = useDispatch();

    const loading = useSelector((state) => state.auditor.loadingFBO);
    const fbo = useSelector((state) => state.auditor.fbo);
    
    const [fboState, setFBOState] = React.useState(fbo);
    const [isDirty, setIsDirty] = React.useState(false);
    const [refreshAction, setRefreshAction] = React.useState(false);
    
    React.useEffect(() => {
	    dispatch(auditorActions.loadAuditor(id, navigate));
    }, [id]);

    React.useEffect(() => {
		setFBOState(fbo);
        setIsDirty(false);
        setRefreshAction(!refreshAction);
                
        if(fbo) {
            reset(fbo.auditor);
        }
        
        clearErrors();
    }, [fbo]);

    const reloadAction = () => {
        setRefreshAction(!refreshAction);
    }

    const onChangeDocument = (isProduct) => {
        setRefreshAction(!refreshAction);
    }

    const getFormattedDate = (value) => {
        if(value) {
            return dateFormat(value, "mm/dd/yyyy");
        }
        return "";
    }

    const getFormattedDateTime = (value) => {
        if(value) {
            return dateFormat(value, "mm/dd/yyyy HH:MM");
        }
        return "";
    }

    const getDateWithoutTimezone = (value) => {
        if(value) {
            const tzoffset = value.getTimezoneOffset() * 60000;
            const withoutTimezone = new Date(value.valueOf() - tzoffset).toISOString().slice(0, -1);
            return withoutTimezone;
        }
        return "";
    }

    const onAuditorChange = (name, value) => {
        var copy_fboState = createDeepClone(fboState);
        
        if(value) {
            if(name === "dateOfBirth") {
                copy_fboState.auditor[name] = getDateWithoutTimezone(value);
            }
            else {
                copy_fboState.auditor[name] = value;
            }
        }
        else if(value === false) {
            copy_fboState.auditor[name] = false;
        }
        else {
            copy_fboState.auditor[name] = null;
        }
        
        setFBOState(copy_fboState);
        setIsDirty(true);
    }

    const onStandardChange = (id, value) => {
        var copy_fboState = createDeepClone(fboState);

        var index =  copy_fboState.standardList.findIndex(x => x.id === id);
        if(index !== -1) {
            copy_fboState.standardList[index].isSelected = value;
            setFBOState(copy_fboState);
            setIsDirty(true);
        }
    }

    const onStandardRoleChange = (id, roleIndex, value) => {
        var copy_fboState = createDeepClone(fboState);

        var index =  copy_fboState.standardList.findIndex(x => x.id === id);
        if(index !== -1) {
            copy_fboState.standardList[index].roleList[roleIndex].isSelected = value;
            setFBOState(copy_fboState);
            setIsDirty(true);
        }
    }

    const onSaveAuditor = async() => {
        const valid = await trigger();
        if(valid) {
            dispatch(auditorActions.saveAuditor(fboState, navigate));
        }
    }

    const onCancelAuditor = () => {
        setFBOState(fbo);
        setIsDirty(false);
        clearErrors();
    }
    
    return(
        <Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={"Edit Auditor"} />
                    <Row>
                        <Col lg={fboState && fboState.auditor.id > 0 ? 9 : 12}>
                            <CardLoader loading={loading} />

                            <Card className="mb-0">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">Auditor</h5>
                                    <div className="flex-shrink-0">
                                        <div className="d-flex flex-wrap gap-2">
                                            <button className="btn btn-primary btn-label"
                                                disabled={loading || !isDirty}
                                                onClick={onSaveAuditor}
                                            >
                                                <i className="ri-save-line label-icon align-middle"></i> Save Auditor
                                            </button>
                                            <button className="btn btn-danger btn-label"
                                                disabled={loading || !isDirty}
                                                onClick={onCancelAuditor}
                                            >
                                                <i className="ri-close-line label-icon align-middle"></i> Cancel
                                            </button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    <Row className="gy-2">
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">First Name</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.firstName) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Last Name</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.lastName) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Email</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.email) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Employee ID</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.employeeId) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Telephone Number</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.telephoneNumber) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Mobile Number</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.userProfile.mobileNumber) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={12}>
                                            <Label className="form-label">Date of Birth</Label>
                                            <Flatpickr className="form-control"
                                                value={getFormattedDateTime((fboState && fboState.auditor.dateOfBirth) || "")}
                                                onChange={([date]) => onAuditorChange("dateOfBirth", date)}
                                                options={{
                                                    enableTime: false,
                                                    dateFormat: "m/d/Y"
                                                }}
                                            />
                                        </Col>
                                        <Col xxl={8} md={8}>
                                            <Label className="form-label">University Major</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.auditor.qualification) || ""}
                                                onChange={e => onAuditorChange("qualification", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={8} md={8}>
                                            <Label className="form-label">Address</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.auditor.address) || ""}
                                                onChange={e => onAuditorChange("address", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">City</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.auditor.city) || ""}
                                                onChange={e => onAuditorChange("city", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">State</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.auditor.state) || ""}
                                                onChange={e => onAuditorChange("state", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Country</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.auditor.country) || ""}
                                                onChange={e => onAuditorChange("country", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Zip Code</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.auditor.zipCode) || ""}
                                                onChange={e => onAuditorChange("zipCode", e.target.value)}
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                            <Card className="mb-0">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">SAC Roles</h5>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    {fboState && fboState.standardList.filter(x => x.isSAC === true).map(function(standard, index){
                                        return (
                                            <Fragment key={index}>
                                                <Col xxl={12} md={12}>
                                                    <div className="form-check mb-1" key={index}>
                                                        <input type="checkbox" className="form-check-input"
                                                            checked={standard.isSelected}
                                                            onChange={e => onStandardChange(standard.id, e.target.checked)}
                                                        />
                                                        <Label className="mt-0">{standard.standardShortName}</Label>
                                                    </div>
                                                </Col>
                                                <Row className={`mt-2 ms-2 mb-2 ${standard.isSelected ? "" : "d-none"}`}>
                                                    {standard.roleList.map(function(role, roleIndex){
                                                        return (
                                                            <Col xxl={2} md={2} key={roleIndex}>
                                                                <div className="form-check mb-1">
                                                                    <input type="checkbox" className="form-check-input success-item"
                                                                        checked={role.isSelected}
                                                                        onChange={e => onStandardRoleChange(standard.id, roleIndex, e.target.checked)}
                                                                    />
                                                                    <Label className="mt-0">{role.name}</Label>
                                                                </div>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            </Fragment>
                                        );
                                    })}
                                </CardBody>
                            </Card>
                            <Card className="mb-0">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">Hellas Roles</h5>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    {fboState && fboState.standardList.filter(x => x.isHellas === true).map(function(standard, index){
                                        return (
                                            <Fragment key={index}>
                                                <Col xxl={12} md={12}>
                                                    <div className="form-check mb-1" key={index}>
                                                        <input type="checkbox" className="form-check-input"
                                                            checked={standard.isSelected}
                                                            onChange={e => onStandardChange(standard.id, e.target.checked)}
                                                        />
                                                        <Label className="mt-0">{standard.standardShortName}</Label>
                                                    </div>
                                                </Col>
                                                <Row className={`mt-2 ms-2 mb-2 ${standard.isSelected ? "" : "d-none"}`}>
                                                    {standard.roleList.map(function(role, roleIndex){
                                                        return (
                                                            <Col xxl={2} md={2} key={roleIndex}>
                                                                <div className="form-check mb-1">
                                                                    <input type="checkbox" className="form-check-input success-item"
                                                                        checked={role.isSelected}
                                                                        onChange={e => onStandardRoleChange(standard.id, roleIndex, e.target.checked)}
                                                                    />
                                                                    <Label className="mt-0">{role.name}</Label>
                                                                </div>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            </Fragment>
                                        );
                                    })}
                                </CardBody>
                            </Card>
                            <Card className="mb-0">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">Vienna Roles</h5>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    {fboState && fboState.standardList.filter(x => x.isVienna === true).map(function(standard, index){
                                        return (
                                            <Fragment key={index}>
                                                <Col xxl={12} md={12}>
                                                    <div className="form-check mb-1" key={index}>
                                                        <input type="checkbox" className="form-check-input"
                                                            checked={standard.isSelected}
                                                            onChange={e => onStandardChange(standard.id, e.target.checked)}
                                                        />
                                                        <Label className="mt-0">{standard.standardShortName}</Label>
                                                    </div>
                                                </Col>
                                                <Row className={`mt-2 ms-2 mb-2 ${standard.isSelected ? "" : "d-none"}`}>
                                                    {standard.roleList.map(function(role, roleIndex){
                                                        return (
                                                            <Col xxl={2} md={2} key={roleIndex}>
                                                                <div className="form-check mb-1">
                                                                    <input type="checkbox" className="form-check-input success-item"
                                                                        checked={role.isSelected}
                                                                        onChange={e => onStandardRoleChange(standard.id, roleIndex, e.target.checked)}
                                                                    />
                                                                    <Label className="mt-0">{role.name}</Label>
                                                                </div>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            </Fragment>
                                        );
                                    })}
                                </CardBody>
                            </Card>
                            <Card className="mb-3">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">Unaccredited Roles</h5>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    {fboState && fboState.standardList.filter(x => x.isUnaccredited === true).map(function(standard, index){
                                        return (
                                            <Fragment key={index}>
                                                <Col xxl={12} md={12}>
                                                    <div className="form-check mb-1" key={index}>
                                                        <input type="checkbox" className="form-check-input"
                                                            checked={standard.isSelected}
                                                            onChange={e => onStandardChange(standard.id, e.target.checked)}
                                                        />
                                                        <Label className="mt-0">{standard.standardShortName}</Label>
                                                    </div>
                                                </Col>
                                                <Row className={`mt-2 ms-2 mb-2 ${standard.isSelected ? "" : "d-none"}`}>
                                                    {standard.roleList.map(function(role, roleIndex){
                                                        return (
                                                            <Col xxl={2} md={2} key={roleIndex}>
                                                                <div className="form-check mb-1">
                                                                    <input type="checkbox" className="form-check-input success-item"
                                                                        checked={role.isSelected}
                                                                        onChange={e => onStandardRoleChange(standard.id, roleIndex, e.target.checked)}
                                                                    />
                                                                    <Label className="mt-0">{role.name}</Label>
                                                                </div>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            </Fragment>
                                        );
                                    })}
                                </CardBody>
                            </Card>
                            {fboState && fboState.auditor.id > 0 &&
                                <ServiceDocumentGrid
                                    serviceId={-2}
                                    refId={fboState.auditor.id}
                                    refName={"AUDITOR"}
                                    parentId={fboState.auditor.id}
                                    parentName={"AUDITOR"}
                                    isProduct={false}
                                    collapsed={"false"}
                                    canAddDocument={true}
                                    canDeleteDocument={true}
                                    canAddCertificate={false}
                                    canDeleteCertificate={false}
                                    onChange={onChangeDocument}
                                />
                            }
                        </Col>
                        
                        {fboState && fboState.auditor.id > 0 &&
                        <Col lg={3}>
                            <ActionList id={fboState.auditor.id} name={"AUDITOR"} refreshAction={refreshAction} />
                        </Col>
                        }
                    </Row>
                </Container>
            </div>
        </Fragment>
    );
}

export default AuditorEditScreen;